import { useState, useCallback, useRef, useEffect } from "react"
import FupQuotaService from "../../../../Service/API/FupQuota/FupQuotaService"
import { QrisPanel } from "./PickerComponent/QrisPanel"
import { VAPanel } from "./PickerComponent/VaPanel"
import DashboardService from "../../../../Service/API/Dashboard/DashboardService"

const qrisLogo = "https://vectorseek.com/wp-content/uploads/2023/09/Quick-Response-Code-Indonesia-Standard-QRIS-Logo-Vector.svg-.png"
const bniLogo = "https://d2v6npc8wmnkqk.cloudfront.net/storage/6613/conversions/BNI-small.webp"

const emptyQrisState = { status: "idle", qrContent: null, validityPeriod: null, error: null }

const CollapsePanel = ({ open, children }) => {
  const ref = useRef(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (ref.current) setHeight(open ? ref.current.scrollHeight : 0)
  }, [open, children])

  return (
    <div className="fup-pm__collapse" style={{ maxHeight: height }}>
      <div ref={ref}>{children}</div>
    </div>
  )
}

/**
 * Payment method picker for a single Fup/Add-On purchase.
 * Mirrors the invoice `PaymentMethodPicker`, but talks to `FupQuotaService`
 * and only exposes the methods the Add-On backend supports: QRIS + BNI VA.
 *
 * Props:
 *  - purchase: the add_on_purchases row (id, addOnName, addOnTypeLabel, amount, virtualAccountBni, ...)
 *  - profile: mitra profile (used for admin-fee display, same shape as invoice profile)
 */
export const PaymentMethodPickerAddOn = ({ purchase, profile = {} }) => {
  const trxId = purchase?.id
  console.log(trxId)
  const total = Number(purchase?.amount ?? 0)

  const [openKey, setOpenKey] = useState("bni")
  const [copied, setCopied] = useState(null)
  const [qrisState, setQrisState] = useState(emptyQrisState)

  const fupQuotaService = useRef(new FupQuotaService()).current
  const dashboardService  = new DashboardService()

  const bniVa = purchase?.virtualAccountBni
  const bank = {
    key: "bni",
    label: "BNI",
    va: bniVa,
    fee: profile?.bniAdminFee,
    logo: bniLogo
  }

  const currPeriod = {
    itemName: purchase?.addOnName,
    periodInvoice: purchase?.addOnTypeLabel || "Add-On",
    id: trxId,
    trx_id: trxId
  }

  const toggle = useCallback((key) => {
    setOpenKey(prev => (prev === key ? null : key))
  }, [])

  const handleCopy = useCallback((val, key) => {
    navigator.clipboard.writeText(String(val || "").replace(/\s/g, "")).catch(() => {})
    setCopied(key)
    setTimeout(() => setCopied(null), 2000)
  }, [])

  const mapQrisResponse = (data = {}) => {
    const info = data.additionalInfo ?? {}
    if (!Object.keys(data).length) return emptyQrisState
    return { status: "done", qrContent: data.qrContent, validityPeriod: info.validityPeriod, error: null }
  }

  const handleFetchQris = useCallback(async () => {
    if (!trxId) return
    setQrisState({ ...emptyQrisState, status: "loading" })
    try {
      const response = await dashboardService.getQrisState(trxId)
      setQrisState(mapQrisResponse(response?.data ?? {}))
    } catch {
      setQrisState(emptyQrisState)
    }
  }, [trxId, fupQuotaService])

  const handleGenerateQris = useCallback(async () => {
    if (!trxId) return
    setQrisState({ ...emptyQrisState, status: "loading" })
    try {
      const response = await dashboardService.postQris(trxId)
      setQrisState(mapQrisResponse(response?.data ?? {}))
    } catch (error) {
      setQrisState({ ...emptyQrisState, status: "error", error: error?.response?.data?.message ?? "Gagal membuat QR Code. Silakan coba lagi." })
    }
  }, [trxId, fupQuotaService])

  const handleRefreshQris = useCallback(() => {
    setQrisState(emptyQrisState)
    handleGenerateQris()
  }, [handleGenerateQris])

  useEffect(() => {
    handleFetchQris()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trxId])

  const chevronClass = (key) => `fup-pm__chevron${openKey === key ? " is-open" : ""}`

  if (!trxId) return null

  return (
    <div className="fup-pm">
      {/* BNI VA */}
      {bniVa ? (
        <div>
          <button
            type="button"
            className="fup-pm__trigger"
            onClick={() => toggle("bni")}
            aria-expanded={openKey === "bni"}
          >
            <img src={bniLogo} alt="BNI" className="fup-pm__logo" onError={e => { e.target.style.display = "none" }} />
            <div className="fup-pm__label-wrap">
              <div className="fup-pm__label">BNI</div>
              <div className="fup-pm__sub">Virtual Account</div>
            </div>
            <i className={`fa fa-chevron-down ${chevronClass("bni")}`} aria-hidden="true" />
          </button>

          <CollapsePanel open={openKey === "bni"}>
            <VAPanel
              bank={bank}
              currPeriod={currPeriod}
              fees={bank.fee}
              total={total}
              copied={copied}
              onCopy={handleCopy}
            />
          </CollapsePanel>
        </div>
      ) : (
        <div className="fup-pm__empty">
          Virtual Account BNI belum tersedia untuk transaksi ini. Gunakan QRIS untuk membayar.
        </div>
      )}
    {/* QRIS */}
    <div className="border-bottom">
        <button
          type="button"
          className="fup-pm__trigger"
          onClick={() => toggle("qris")}
          aria-expanded={openKey === "qris"}
        >
          <div className="fup-pm__logo-box">
            <img src={qrisLogo} alt="QRIS" className="fup-pm__logo" onError={e => { e.target.style.display = "none" }} />
          </div>
          <div className="fup-pm__label-wrap">
            <div className="fup-pm__label">QRIS</div>
            <div className="fup-pm__sub">GoPay · OVO · Dana · Semua Bank</div>
          </div>
          <i className={`fa fa-chevron-down ${chevronClass("qris")}`} aria-hidden="true" />
        </button>

        <CollapsePanel open={openKey === "qris"}>
          <QrisPanel
            currPeriod={currPeriod}
            total={total}
            qrisState={qrisState}
            profile={profile}
            onGenerate={handleGenerateQris}
            onRefresh={handleRefreshQris}
          />
        </CollapsePanel>
      </div>
    </div>
  )
}

export default PaymentMethodPickerAddOn